import { FormControl, Grid, InputLabel, MenuItem, Select } from "@material-ui/core";
import { createStyles } from "@material-ui/core/styles";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Bagde } from "components/Card";
import Dialog from "components/Dialog";
import TextField from "components/TextField";
import { DATE_FORMAT_PRINT } from "config/constants";
import useText from "constants/text";
import { SubTask, SubTaskType } from "generated/graphql";
import { CalendarIcon, DeliveryIcon, PickUpIcon } from "icons";
import moment from "moment";
import React from "react";
import { isWeekEnd } from "utils";

const currentHours = new Date().getHours();
export interface TaskModificationProps {
  open: boolean;
  handleClose: () => void;
  task?: SubTask;
  handleConfirm?: (reprogram: { date: string; time: string }, task?: SubTask) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: "100%",
      height: 200,
    },
    top: {
      display: "flex",
    },
    caisse: {
      width: 58,
      height: 58,
      display: "flex",
      justifyItems: "center",
      justifyContent: "center",
      opacity: 0.6,
      borderRadius: 18,
      marginRight: 12.5,
    },
    name: {
      fontSize: 12,
      fontWeight: 500,
      color: "#707070",
    },
    type: {
      fontSize: 21,
      fontWeight: 800,
      color: "#399ebf",
    },
    nameContent: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    detail: {
      display: "flex",
      flexDirection: "column",
      marginLeft: "auto",
      justifyContent: "space-between",
    },
    time: {
      color: "#8f95ad",
      fontSize: 14,
      marginLeft: 12.5,
    },
    number: {
      color: "#707070",
      fontSize: 14,
      marginLeft: 12.5,
    },
    bloc: {
      display: "flex",
      width: 200,
    },
    icon: {
      marginLeft: 2,
    },
  })
);
const defaultForm = {
  date: moment().format("YYYY-MM-DD"),
  time: "11:00",
};

export function TaskModification({
  handleClose,
  task,
  open,
  handleConfirm,
}: TaskModificationProps) {
  const styles = useStyles();
  const { text } = useText();
  const [formValues, setFormValues] = React.useState(defaultForm);
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setFormValues((prev: any) => ({
      ...prev,
      [event.target.name]: isWeekEnd(event.target.value)
        ? moment(event.target.value).add(1, "days").format("YYYY-MM-DD")
        : event.target.value,
    }));
  };

  React.useEffect(() => {
    if (task) {
      setFormValues((prev) => ({ ...prev, date: task.subtask_execution_date }));
    }
  }, [task]);

  const minDay = React.useMemo(() => {
    let min = moment(task?.subtask_execution_date).format("YYYY-MM-DD");
    if (isWeekEnd(min) || currentHours >= 15) {
      min = moment(min).add(1, "days").format("YYYY-MM-DD");
    }
    if (isWeekEnd(min)) {
      min = moment(min).add(1, "days").format("YYYY-MM-DD");
    }
    return min;
  }, [task]);

  const isOkForSameDay = () => {
    const date = moment(task?.subtask_execution_date).isSame(new Date());
    return date;
  };
  return (
    <>
      <Dialog
        title="Task Rescheduling"
        open={open}
        handleClose={() => handleClose()}
        maxWidth="lg"
        key="Task Rescheduling"
        cancelButtonTitle="EXIT"
        handleConfirm={() => {
          handleConfirm(formValues, task);
        }}
      >
        <Grid container className={styles.root}>
          <Grid item xs={6}>
            <div className={styles.top}>
              <div
                style={{
                  background: task?.subtask_type === SubTaskType.Pickup ? "#e3f8fa" : "#faecca",
                }}
                className={styles.caisse}
              >
                <div
                  style={{
                    marginTop: "auto",
                    marginBottom: "auto",
                  }}
                >
                  {task?.subtask_type === SubTaskType.Pickup ? <PickUpIcon /> : <DeliveryIcon />}
                </div>
              </div>
              <div className={styles.nameContent}>
                <div className={styles.type}>{task?.subtask_type}</div>
              </div>
              <div className={styles.detail}>
                <div className={styles.bloc}>
                  <div className={styles.icon}>
                    <Bagde
                      variante="contained"
                      value={task?.subtask_operation_on_parcel?.length.toString() ?? "0"}
                      height={20}
                      width={20}
                      fontSize={8}
                    />
                  </div>
                  <div className={styles.number}>
                    {text.parcel.parcels_to} {text.all[task?.subtask_type.toLocaleLowerCase()]}
                  </div>
                </div>
                <div className={styles.bloc}>
                  <div
                    className={styles.icon}
                    style={{
                      marginLeft: 5,
                    }}
                  >
                    <CalendarIcon />
                  </div>
                  <div className={styles.time}>
                    {`${moment(task?.subtask_execution_date).format(DATE_FORMAT_PRINT)} ${
                      task?.subtask_execution_slot_start
                    }`}
                  </div>
                </div>
              </div>
            </div>
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <TextField
              type="date"
              fullWidth
              label="Date"
              name="date"
              value={formValues.date}
              onChange={onChange}
              inputProps={{ min: minDay }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="demo-simple-select-outlined-label">Time</InputLabel>
              <Select
                labelId="demo-simple-select-outlined-label"
                id="demo-simple-select-outlined"
                label="Time"
                name="time"
                value={formValues.time}
                onChange={(e) => {
                  setFormValues((prev) => ({ ...prev, time: e.target.value as string }));
                }}
              >
                <MenuItem value="11:00" disabled={isOkForSameDay() && currentHours >= 11}>
                  11:00 - 13:00
                </MenuItem>
                <MenuItem value="15:00" disabled={isOkForSameDay() && currentHours >= 15}>
                  15:00 - 17:00
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Dialog>
    </>
  );
}
